import { EncounterFeedType, PanelKey_ProvideInfo } from '@app/advisor-v5-shared';
import { Logger } from '@nu-art/logger';
import { BadImplementationException, capitalizeFirstLetter, dbObjectToId, exists, formatTimestamp } from '@nu-art/ts-common';
import { ModuleFE_RuntimeEncounterFeed } from '../../modules/runtime/encounter-feed/ModuleFE_RuntimeEncounterFeed.js';
import { ModuleFE_Advisor_Action, ModuleFE_Advisor_Pathway, ModuleFE_Advisor_PathwayState, ModuleFE_Advisor_Resource, ModuleFE_EMInterface, ModuleFE_RuntimeActiveNavState, ModuleFE_RuntimeOrder, ModuleFE_RuntimeProtocol } from '@app/advisor-frontend';
import { ConsiderationGroupEnum, RuntimeOrderStatus_Enum } from '@app/advisor-shared';
import { PathwayStateType_Admit, PathwayStateType_Discharge, PathwayStateType_Initial, pathwayType_SingleState } from '@app/pathway-shared';
import { ModuleFE_PanelFindings } from '../../modules/ModuleFE_PanelFindings/ModuleFE_PanelFindings.js';
import { ProtocolData } from '../../_utils/ProtocolData/ProtocolData.js';
import { ModuleFE_ProvideInfo } from '../../modules/provide-info/ModuleFE_ProvideInfo.js';
import { ProtocolStatus_AwaitingInfo } from '../../_utils/ProtocolData/types.js';
import { ModuleFE_AdvisorMini } from '../../modules/ModuleFE_AdvisorMini.js';
export class EncounterFeedItem extends Logger {
    static modulesToAwait = [
        //Static Modules
        ModuleFE_Advisor_Action,
        ModuleFE_Advisor_PathwayState,
        //Runtime Modules
        ModuleFE_RuntimeEncounterFeed,
        ModuleFE_RuntimeOrder,
        ModuleFE_RuntimeActiveNavState,
    ];
    //######################### Instantiation #########################
    feedItem;
    constructor(item) {
        super(`EncounterFeedItem_${item._id.substring(0, 9)}`);
        this.feedItem = item;
    }
    static createNew = (feedItemId) => {
        const feedItem = ModuleFE_RuntimeEncounterFeed.cache.unique(feedItemId);
        if (!feedItem)
            return;
        return new EncounterFeedItem(feedItem);
    };
    //######################### Internal Logic #########################
    onClick_ProtocolNavigation = () => {
        if (this.feedItem.itemType !== EncounterFeedType.ProtocolNavigation)
            throw new BadImplementationException(`Called onClick_ProtocolNavigation when type of feed item is ${this.feedItem.itemType}`);
        const pathwayId = this.feedItem.payload.pathwayId;
        const protocol = ModuleFE_RuntimeProtocol.cache.find(p => p.pathwayId === pathwayId);
        const protocolData = ProtocolData.createNew(protocol?._id);
        if (!protocolData)
            return;
        //Open protocol context
        ModuleFE_ProvideInfo.protocol.open(protocolData.protocol._id, protocolData.getStatus() === ProtocolStatus_AwaitingInfo);
        if (ModuleFE_AdvisorMini.getIsMiniActive()) //Open panel in mini
            ModuleFE_AdvisorMini.openPanel(PanelKey_ProvideInfo);
    };
    onClick_ProtocolConsiderationSwitch = () => {
        if (this.feedItem.itemType !== EncounterFeedType.ProtocolConsiderationSwitch)
            throw new BadImplementationException(`Called onClick_ProtocolConsiderationSwitch when type of feed item is ${this.feedItem.itemType}`);
        const protocolData = ProtocolData.createNew(this.feedItem.payload.protocolId);
        if (!protocolData)
            return;
        //Open protocol context
        ModuleFE_ProvideInfo.protocol.open(protocolData.protocol._id, protocolData.getStatus() === ProtocolStatus_AwaitingInfo);
        if (ModuleFE_AdvisorMini.getIsMiniActive()) //Open panel in mini
            ModuleFE_AdvisorMini.openPanel(PanelKey_ProvideInfo);
    };
    onClick_OrderResulted = () => {
        if (this.feedItem.itemType !== EncounterFeedType.OrderResulted)
            throw new BadImplementationException(`Called onClick_OrderResulted when type of feed item is ${this.feedItem.itemType}`);
        const rtOrderId = this.feedItem.payload.runtimeId;
        const jumped = ModuleFE_PanelFindings.jumpToFinding(rtOrderId);
        if (!jumped) //We did not jump to any finding
            return;
        //We jumped to finding
        const rtOrder = ModuleFE_RuntimeOrder.cache.unique(rtOrderId);
        if (rtOrder && rtOrder.orderStatus === RuntimeOrderStatus_Enum.RESULTED) //Mark order as completed
            ModuleFE_EMInterface.vv1.orders.completeOrders({ runtimeOrderIds: [this.feedItem.payload.runtimeId] }).executeSync();
    };
    //######################### Public Logic #########################
    resolveLabel = () => {
        switch (this.feedItem.itemType) {
            case EncounterFeedType.OrderSent: {
                const runtimeOrder = ModuleFE_RuntimeOrder.cache.unique(this.feedItem.payload.runtimeId);
                const dbOrder = ModuleFE_Advisor_Action.cache.unique(runtimeOrder?.orderId);
                const label = dbOrder?.name || dbOrder?.label || 'N/A';
                return [{ text: `${capitalizeFirstLetter(label)} ordered` }];
            }
            case EncounterFeedType.OrderResulted: {
                const runtimeOrder = ModuleFE_RuntimeOrder.cache.unique(this.feedItem.payload.runtimeId);
                const dbOrder = ModuleFE_Advisor_Action.cache.unique(runtimeOrder?.orderId);
                const resource = ModuleFE_Advisor_Resource.cache.unique(dbOrder?.resourceId);
                const label = dbOrder?.name || dbOrder?.label || 'N/A';
                //UI-HACK: treatment orders should say "ordered" always
                return [{
                        text: resource?.type === 'Procedures'
                            ? `${capitalizeFirstLetter(label)} ordered`
                            : `${capitalizeFirstLetter(label)} resulted`
                    }];
            }
            case EncounterFeedType.ProtocolConsiderationSwitch: {
                switch (this.feedItem.payload.considerationLevel) {
                    case ConsiderationGroupEnum.HIGH_CONSIDERATION:
                        return [{ text: 'Protocol moved to high consideration' }];
                    case ConsiderationGroupEnum.LOW_CONSIDERATION:
                        return [{ text: 'Protocol moved to low consideration' }];
                    case ConsiderationGroupEnum.BELOW_THRESHOLD:
                        return [{ text: 'Protocol moved to below threshold' }];
                    default:
                        return [{ text: 'Protocol moved to unknown consideration level' }];
                }
            }
            case EncounterFeedType.ProtocolNavigation: {
                const pathwayId = this.feedItem.payload.pathwayId;
                const protocol = ModuleFE_RuntimeProtocol.cache.find(p => p.pathwayId === pathwayId);
                const pathway = ModuleFE_Advisor_Pathway.cache.unique(protocol?.pathwayId);
                if (pathway?.type === pathwayType_SingleState)
                    return [{ text: 'Protocol Started' }];
                const activeState = ModuleFE_RuntimeActiveNavState.cache.unique(this.feedItem.payload.stateId);
                const dbState = ModuleFE_Advisor_PathwayState.cache.unique(activeState?.stateDbId);
                switch (dbState?.type) {
                    case PathwayStateType_Initial:
                        return [{ text: 'Protocol Started' }];
                    case PathwayStateType_Admit:
                        return [{ text: 'Protocol Reached Admission' }];
                    case PathwayStateType_Discharge:
                        return [{ text: 'Protocol Reached Discharge' }];
                    default:
                        if (!dbState)
                            return [{ text: 'Protocol navigated' }];
                        return [{ text: 'Protocol navigated to ' }, { text: dbState.label ?? dbState.name, unique: true }];
                }
            }
        }
    };
    resolveTimestamp = () => {
        const todayCutOff = new Date().setHours(0, 0, 0, 0);
        if (this.feedItem.__created >= todayCutOff)
            return formatTimestamp('hh:mmA', this.feedItem.__created);
        return formatTimestamp('MMM DD, YYYY · hh:mmA', this.feedItem.__created);
    };
    resolveProtocolIds = () => {
        switch (this.feedItem.itemType) {
            case EncounterFeedType.OrderSent:
            case EncounterFeedType.OrderResulted: {
                const pathwayIds = this.feedItem.payload.relatedPathwayIds;
                const protocols = ModuleFE_RuntimeProtocol.cache.filter(p => pathwayIds.includes(p.pathwayId));
                return protocols.map(dbObjectToId);
            }
            case EncounterFeedType.ProtocolNavigation: {
                const pathwayId = this.feedItem.payload.pathwayId;
                const protocol = ModuleFE_RuntimeProtocol.cache.find(p => p.pathwayId === pathwayId);
                return exists(protocol) ? [protocol._id] : [];
            }
            case EncounterFeedType.ProtocolConsiderationSwitch:
                return [this.feedItem.payload.protocolId];
        }
    };
    onClick = (e) => {
        e.stopPropagation();
        switch (this.feedItem.itemType) {
            case EncounterFeedType.ProtocolNavigation:
                return this.onClick_ProtocolNavigation();
            case EncounterFeedType.ProtocolConsiderationSwitch:
                return this.onClick_ProtocolConsiderationSwitch();
            case EncounterFeedType.OrderResulted:
                return this.onClick_OrderResulted();
            case EncounterFeedType.OrderSent:
            default:
                return;
        }
    };
}
//# sourceMappingURL=EncounterFeedItem.js.map